import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRecoilState } from 'recoil';

import { schedulerApi } from '../api';
import { Routine, RoutineActivity } from '@demind-inc/core';
import { cloneDeep } from 'lodash';
import { trackEventMixpanel } from '../../utils';
import { eventsSnackBarAtom } from '../recoil';
import { translateI18n } from '../../i18n';

interface UseHideRoutineParams {
  routineId: string;
  activityId: string;
}

export const useHideRoutine = () => {
  const queryClient = useQueryClient();
  const [_, setEventsSnackbar] = useRecoilState(eventsSnackBarAtom);

  const hideRoutineMutation = useMutation({
    mutationFn: ({ routineId, activityId }: UseHideRoutineParams) => {
      return schedulerApi.hideRoutineActivity(routineId, activityId).then(({ data }) => data);
    },
    onMutate: async ({ routineId, activityId }: UseHideRoutineParams) => {
      setEventsSnackbar({
        message: translateI18n('routine.snackbar.deleting'),
        autoHideDuration: 'disable',
        type: 'loading',
      });

      await queryClient.cancelQueries({
        queryKey: ['lifestack.scheduler.routine', { routineId }],
      });
      queryClient.setQueriesData<Routine>(
        { queryKey: ['lifestack.scheduler.routine', { routineId }] },
        (prev) => {
          const newActivities = cloneDeep(prev?.routineActivities ?? []).map((activity) => {
            if (activity.routineActivityId === activityId) {
              return { ...activity, hidden: true } as RoutineActivity;
            }
            return activity;
          });

          return { ...prev, routineActivities: newActivities } as Routine;
        }
      );
    },
    onSuccess: (_, { routineId, activityId }) => {
      trackEventMixpanel('delete_routine', {
        routineId,
        activityId,
      });
      queryClient.invalidateQueries({ queryKey: ['lifestack.scheduler.routine', { routineId }] });
      queryClient.invalidateQueries({ queryKey: ['lifestack.calendar.events'] });
      setEventsSnackbar({ message: translateI18n('routine.snackbar.deleted'), type: 'success' });
    },
    onError: () => {
      setEventsSnackbar({ message: translateI18n('routine.snackbar.deleteFailed'), type: 'error' });
    },
  });

  return {
    hideRoutine: hideRoutineMutation.mutateAsync,
    ...hideRoutineMutation,
  };
};
